import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { Observable, map } from 'rxjs';
import {
  CreateOrderReq,
  DeliveryForDate,
  Order,
  OrderConfirmReq,
  OrderItem,
  OrderPaymentStatus,
  OrderType,
} from '../api/contracts/order';
import { HttpMethods } from '../api/api.enums';
import { environment } from 'src/environments/environment';
import { Cart } from '../types/cart';
import { PaymentMethod } from '../types/payment';
import { OrderItemStatus } from '../types/order';
import { DeliveryForDateStatus } from '../types/delivery-for-date';

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  constructor(private readonly apiService: ApiService) {}

  retrieveOrders(): Observable<Order[]> {
    return this.apiService
      .request<Order[]>({
        method: HttpMethods.GET,
        url: `${environment.apiUrl}/orders`,
      })
      .pipe(map((res) => res.body as Order[]));
  }

  retrieveOrder(id: string): Observable<Order> {
    return this.apiService
      .request<Order>({
        method: HttpMethods.GET,
        url: `${environment.apiUrl}/orders/${id}`,
      })
      .pipe(map((res) => res.body as Order));
  }

  postNewOrder(orderReq: CreateOrderReq): Observable<Order> {
    return this.apiService
      .request<Order, null, CreateOrderReq>({
        method: HttpMethods.POST,
        url: `${environment.apiUrl}/orders`,
        body: orderReq,
      })
      .pipe(map((res) => res.body as Order));
  }

  createOrderRequestObjectFromCart(
    cart: Cart,
    paymentMethod: PaymentMethod,
    locationHash: string
  ): CreateOrderReq {
    return {
      type: cart.orderType,
      items: cart.items.map((item) => {
        return {
          day: item.day,
          itemId: item.product.id,
          quantity: item.quantity,
          weeklyMenuId: item.weeklyMenuId,
        };
      }),
      paymentType: paymentMethod,
      locationHash,
    };
  }

  confirmOrderItemReceiving(barcode: number): Observable<void> {
    return this.apiService
      .request<void, null, OrderConfirmReq>({
        method: HttpMethods.POST,
        url: `${environment.apiUrl}/confirm_item_delivery`,
        body: {
          barcode,
        },
      })
      .pipe(map((res) => void 0));
  }

  retrieveDeliveriesForDate(date: string): Observable<DeliveryForDate[]> {
    // format is 2022-12-22
    return this.apiService
      .request<DeliveryForDate[]>({
        method: HttpMethods.GET,
        url: `${environment.apiUrl}/orders/deliveries_for_date/${date}`,
      })
      .pipe(map((res) => res.body as DeliveryForDate[]));
  }

  getOrderItemStatus(order: Order, orderItem: OrderItem): OrderItemStatus {
    if (
      order.paymentStatus === OrderPaymentStatus.PAID &&
      orderItem.delivered === true &&
      orderItem.deliveryConfirmed === true
    ) {
      return OrderItemStatus.CONFIRMED;
    }

    if (
      order.paymentStatus === OrderPaymentStatus.PAID &&
      orderItem.delivered === true &&
      orderItem.deliveryConfirmed === false &&
      order.type === OrderType.DELIVERY
    ) {
      return OrderItemStatus.DELIVERED;
    }

    if (
      order.paymentStatus === OrderPaymentStatus.PAID &&
      orderItem.delivered === true &&
      order.type === OrderType.IN_STORE
    ) {
      return OrderItemStatus.PICKED_UP;
    }

    if (
      order.paymentStatus === OrderPaymentStatus.PAID &&
      orderItem.delivered === false
    ) {
      return OrderItemStatus.PENDING;
    }

    if (order.paymentStatus === OrderPaymentStatus.AWAITING_PAYMENT) {
      return OrderItemStatus.AWAITING_PAYMENT;
    }

    if (
      order.paymentStatus === OrderPaymentStatus.PAID &&
      orderItem.refundRequested === true
    ) {
      return OrderItemStatus.REFUNDED;
    }

    if (
      order.paymentStatus === OrderPaymentStatus.CAPTURE_FAILED ||
      order.paymentStatus === OrderPaymentStatus.AUTHORIZATION_FAILED
    ) {
      return OrderItemStatus.PAYMENT_ERROR;
    }

    return OrderItemStatus.PAYMENT_ERROR;
  }

  getOrderStatus(order: Order): OrderItemStatus {
    if (order.items.every((i) => i.deliveryConfirmed)) {
      return OrderItemStatus.CONFIRMED;
    }

    if (
      order.type === OrderType.DELIVERY &&
      order.items.every((i) => i.delivered)
    ) {
      return OrderItemStatus.DELIVERED;
    }

    if (
      order.type === OrderType.IN_STORE &&
      order.items.every((i) => i.delivered)
    ) {
      return OrderItemStatus.PICKED_UP;
    }

    if (order.paymentStatus === OrderPaymentStatus.PAID) {
      return OrderItemStatus.PENDING;
    }

    if (order.paymentStatus === OrderPaymentStatus.AWAITING_PAYMENT) {
      return OrderItemStatus.AWAITING_PAYMENT;
    }

    if (order.refundRequested) {
      return OrderItemStatus.REFUNDED;
    }

    return OrderItemStatus.PAYMENT_ERROR;
  }

  getDeliveryForDateStatus(
    deliveryItem: DeliveryForDate
  ): DeliveryForDateStatus {
    if (deliveryItem.refundRequested === true) {
      return DeliveryForDateStatus.REFUNDED;
    }

    if (deliveryItem.deliveryConfirmed === true) {
      return DeliveryForDateStatus.CONFIRMED;
    }

    if (deliveryItem.delivered === true) {
      return DeliveryForDateStatus.DELIVERED;
    }

    if (deliveryItem.delivered === false && !!deliveryItem.deliveryDate) {
      return DeliveryForDateStatus.PENDING;
    }

    return DeliveryForDateStatus.ERROR;
  }
}
